import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card, Button, Input, InputNumber, Select, Tag, Typography,
  Divider, notification, Space, Row, Col, Switch, Empty, Tabs, Tooltip, Modal, Spin, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined,
  ArrowUpOutlined, ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined,
  ArrowLeftOutlined as BackIcon, DragOutlined, PictureOutlined,
  ZoomInOutlined, ZoomOutOutlined, EyeOutlined, SaveOutlined, FolderOpenOutlined,
  ExclamationCircleOutlined, ReloadOutlined, ThunderboltOutlined, ToolOutlined,
  BlockOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Text, Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const FONT_FAMILIES = [
  { label: 'Noto Sans Devanagari',  value: 'Noto Sans Devanagari' },
  { label: 'Noto Serif Devanagari', value: 'Noto Serif Devanagari' },
  { label: 'Baloo 2',               value: 'Baloo 2' },
  { label: 'Hind',                  value: 'Hind' },
  { label: 'Tiro Devanagari Hindi', value: 'Tiro Devanagari Hindi' },
  { label: 'Aakar',                 value: 'Aakar' },
  { label: 'Mukta',                 value: 'Mukta' },
  { label: 'Rajdhani',              value: 'Rajdhani' },
  { label: 'Rozha One',             value: 'Rozha One' },
  { label: 'Shrikhand',             value: 'Shrikhand' },
  { label: 'Yatra One',             value: 'Yatra One' },
  { label: 'Laila',                 value: 'Laila' },
  { label: 'Kalam',                 value: 'Kalam' },
  { label: 'Galada',                value: 'Galada' },
  { label: 'Poppins',               value: 'Poppins' },
  { label: 'Arial',                 value: 'Arial' },
];

const QUICK_TOKENS = [
  'name', 'father_name', 'village', 'district', 'tehsil',
  'phone', 'address', 'occupation', 'relation',
];

const DEFAULT_FONT = 'Noto Sans Devanagari';
const genId = () => `f_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
const DRAFT_KEY = 'poster_builder_draft';

const extractTokens = (fields) => {
  const set = new Set();
  fields.forEach(f => {
    const matches = (f.template || '').match(/\{(\w+)\}/g) || [];
    matches.forEach(m => set.add(m.replace(/[{}]/g, '')));
  });
  return [...set];
};

const renderWithValues = (template, values) =>
  (template || '').replace(/\{(\w+)\}/g, (_, k) => values[k] || '');

// Human-friendly label for a token: field_1 → "Field 1", name → "name"
const tokenLabel = (token) => {
  const m = token.match(/^field_(\d+)$/);
  return m ? `Field ${m[1]}` : token;
};

const buildImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  let clean = imagePath.replace(/^resources\//, '').replace(/^\//, '');
  if (!clean.startsWith('uploads/')) clean = 'uploads/' + clean;
  const base = baseUrl.split('/api')[0];
  return `${base}/${clean}`;
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

// ─────────────────────────────────────────────────────────────────────────────
// QUICK GENERATE — simplified panel for generating poster per person
// ─────────────────────────────────────────────────────────────────────────────
function QuickGenerate({ templates, templatesLoading, fetchTemplates }) {
  const [selTemplate, setSelTemplate]     = useState(null);
  const [genValues, setGenValues]         = useState({});
  const [overlayFiles, setOverlayFiles]   = useState([]);  // regular overlays (is_slot: false)
  const [slotFiles, setSlotFiles]         = useState([]);  // image slot uploads (is_slot: true)
  const [previewing, setPreviewing]       = useState(false);
  const [downloading, setDownloading]     = useState(false);
  const [previewModal, setPreviewModal]   = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [canvasWidth, setCanvasWidth]     = useState(0);
  const [canvasHeight, setCanvasHeight]   = useState(0);
  const [showTemplateList, setShowTemplateList] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRefs = useRef({});
  const slotInputRefs = useRef({});

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await axios.delete(`${baseUrl}/poster-builder-template/${id}`, { headers: authHeaders() });
      notification.success({ message: `Template "${name}" deleted` });
      if (selTemplate?.id === id) { setSelTemplate(null); setGenValues({}); setOverlayFiles([]); }
      fetchTemplates();
    } catch (err) {
      notification.error({ message: 'Delete failed: ' + err.message });
    }
  };

  // tokens from selected template's fields
  const tokens = selTemplate ? extractTokens(selTemplate.fields_config || []) : [];

  // canvas width for text scaling
  useEffect(() => {
    if (!canvasRef.current) return;
    const m = () => setCanvasWidth(canvasRef.current?.offsetWidth || 0);
    m();
    window.addEventListener('resize', m);
    return () => window.removeEventListener('resize', m);
  }, [selTemplate]);

  const handleSelectTemplate = (id) => {
    if (!id) { setSelTemplate(null); setGenValues({}); setOverlayFiles([]); setSlotFiles([]); return; }
    const tpl = templates.find(t => String(t.id) === String(id));
    if (!tpl) return;
    setSelTemplate(tpl);
    setGenValues({});
    const allOverlays = tpl.overlay_config || [];
    // Split into image slots (is_slot:true) and regular overlays
    setSlotFiles(allOverlays.filter(o => o.is_slot).map(s => ({ ...s, file: null, previewUrl: null })));
    setOverlayFiles(allOverlays.filter(o => !o.is_slot).map(ov => ({ ...ov, file: null, previewUrl: null })));
  };

  const handleSlotFile = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setSlotFiles(prev => prev.map((s, i) => i === idx ? { ...s, file, previewUrl, name: file.name } : s));
    e.target.value = '';
  };

  const handleOverlayFile = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setOverlayFiles(prev => prev.map((ov, i) =>
      i === idx ? { ...ov, file, previewUrl, name: file.name } : ov
    ));
    e.target.value = '';
  };

  const canvasImageUrl = selTemplate?.poster_path ? buildImageUrl(selTemplate.poster_path) : null;
  const fields = selTemplate?.fields_config || [];

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('poster_path', selTemplate.poster_path);
    fd.append('text_config', JSON.stringify({ rows: fields }));
    fd.append('member_data', JSON.stringify(genValues));
    // Combine regular overlays + image slots with proper configs
    const allConfigs = [
      ...overlayFiles.map((ov, i) => ({ index: i, top_pct: ov.top_pct, left_pct: ov.left_pct, size_pct: ov.size_pct })),
      ...slotFiles.map((s, i) => ({ index: overlayFiles.length + i, top_pct: s.top_pct, left_pct: s.left_pct, width_pct: s.width_pct, height_pct: s.height_pct, border_width: s.border_width || 0, border_color: s.border_color || '#ffffff', corner_radius: s.corner_radius || 0 })),
    ];
    fd.append('overlay_configs', JSON.stringify(allConfigs));
    overlayFiles.forEach((ov, i) => { if (ov.file) fd.append(`overlay_${i}`, ov.file, ov.name || `ov_${i}.png`); });
    slotFiles.forEach((s, i) => { if (s.file) fd.append(`overlay_${overlayFiles.length + i}`, s.file, s.name || `slot_${i}.jpg`); });
    return fd;
  };

  const triggerDownload = (blobUrl, fileName) => {
    const a = document.createElement('a');
    a.href = blobUrl; a.download = fileName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handlePreview = async () => {
    if (!selTemplate) { notification.warning({ message: 'Select a template first' }); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${baseUrl}/poster/generate-download`, buildFormData(), {
        headers: authHeaders(), responseType: 'blob',
      });
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' }));
      setPreviewBlobUrl(url);
      setPreviewModal(true);
    } catch (err) {
      notification.error({ message: 'Preview failed: ' + err.message });
    }
    setPreviewing(false);
  };

  const handleDownload = async () => {
    if (!selTemplate) { notification.warning({ message: 'Select a template first' }); return; }
    setDownloading(true);
    try {
      const res = await axios.post(`${baseUrl}/poster/generate-download`, buildFormData(), {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' }));
      triggerDownload(url, `${genValues[tokens[0]] || selTemplate.name || 'poster'}.jpg`);
      URL.revokeObjectURL(url);
      notification.success({ message: 'Downloaded! Fill next person\'s details to generate another.' });
      // Clear only values and files — keep template selected for next person
      setGenValues({});
      setOverlayFiles(prev => prev.map(ov => {
        if (ov.previewUrl) URL.revokeObjectURL(ov.previewUrl);
        return { ...ov, file: null, previewUrl: null };
      }));
    } catch (err) {
      notification.error({ message: 'Failed: ' + err.message });
    }
    setDownloading(false);
  };

  return (
    <Row gutter={20}>
      {/* Canvas preview */}
      <Col xs={24} lg={14}>
        <Card>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Text strong>Select Template</Text>
              <Space size={4}>
                <Button size="small" icon={<ReloadOutlined />} onClick={fetchTemplates} loading={templatesLoading} />
                <Button
                  size="small"
                  type={showTemplateList ? 'primary' : 'default'}
                  onClick={() => setShowTemplateList(v => !v)}
                >
                  {showTemplateList ? 'Hide List' : 'Manage Templates'}
                </Button>
              </Space>
            </div>

            <Select
              placeholder="Choose a saved template..."
              style={{ width: '100%' }}
              onChange={handleSelectTemplate}
              showSearch
              allowClear
              onClear={() => handleSelectTemplate(null)}
              optionFilterProp="children"
              loading={templatesLoading}
              value={selTemplate ? String(selTemplate.id) : undefined}
            >
              {templates.map(t => (
                <Option key={t.id} value={String(t.id)}>
                  {t.name}
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                    ({(t.fields_config || []).length} fields)
                  </Text>
                </Option>
              ))}
            </Select>

            {/* Template list with delete buttons */}
            {showTemplateList && (
              <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 6 }}>
                {templates.length === 0 && (
                  <div style={{ padding: 12, textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>No templates saved yet</Text>
                  </div>
                )}
                {templates.map(t => (
                  <div
                    key={t.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
                      background: selTemplate?.id === t.id ? '#e6f4ff' : '#fff',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text strong style={{ fontSize: 13, display: 'block' }}>{t.name}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {t.poster_name || '—'} &bull; {(t.fields_config || []).length} field{(t.fields_config || []).length !== 1 ? 's' : ''}
                      </Text>
                    </div>
                    <Space size={4}>
                      <Button
                        size="small" type="primary" ghost
                        onClick={() => { handleSelectTemplate(String(t.id)); setShowTemplateList(false); }}
                      >
                        Use
                      </Button>
                      <Button
                        size="small" danger icon={<DeleteOutlined />}
                        onClick={() => deleteTemplate(t.id, t.name)}
                      />
                    </Space>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* Canvas */}
          <div
            ref={canvasRef}
            style={{
              position: 'relative', width: '100%',
              border: '2px solid #1890ff', borderRadius: 6,
              overflow: 'hidden', background: '#ddd', minHeight: 300,
            }}
          >
            {canvasImageUrl
              ? <img src={canvasImageUrl} alt="Poster" style={{ width: '100%', display: 'block' }} draggable={false}
                  onLoad={(e) => setCanvasHeight(e.target.offsetHeight)} />
              : (
                <div style={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Empty description="Select a template to preview" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )
            }

            {/* Render text fields — show typed value or a numbered placeholder badge */}
            {fields.map((f, idx) => {
              const scaledSize = Math.max(6, Math.round(f.font_size * (canvasWidth > 0 ? canvasWidth / 2400 : 0.07)));
              const displayText = renderWithValues(f.template, genValues);
              const isEmpty = !displayText;
              return (
                <div
                  key={f.id}
                  style={{
                    position: 'absolute',
                    top: `${f.top_pct}%`,
                    left: `${f.left_pct ?? 50}%`,
                    transform: (f.text_anchor || 'middle') === 'middle' ? 'translateX(-50%)'
                      : f.text_anchor === 'end' ? 'translateX(-100%)' : 'none',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                  }}
                >
                  {isEmpty ? (
                    // Show a numbered badge so user knows which field goes here
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: 'rgba(24,144,255,0.85)', color: '#fff',
                      borderRadius: 12, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                    }}>
                      <span style={{ fontSize: 10, opacity: 0.8 }}>▼</span>
                      {tokenLabel(extractTokens([f])[0] || `field_${idx + 1}`)}
                    </div>
                  ) : (
                    <span style={{
                      fontSize: scaledSize,
                      fontWeight: f.bold ? 'bold' : 'normal',
                      fontFamily: `'${f.font_family || DEFAULT_FONT}', Arial, sans-serif`,
                      color: f.color || '#000',
                      textShadow: '0 0 2px rgba(255,255,255,0.5)',
                    }}>
                      {displayText}
                    </span>
                  )}
                </div>
              );
            })}

            {/* Overlay previews — no editing borders */}
            {overlayFiles.filter(ov => ov.previewUrl).map(ov => (
              <div
                key={ov.id}
                style={{
                  position: 'absolute',
                  top: `${ov.top_pct}%`, left: `${ov.left_pct}%`,
                  width: `${ov.size_pct}%`, pointerEvents: 'none', zIndex: 10,
                }}
              >
                <img src={ov.previewUrl} alt="" draggable={false}
                  style={{ width: '100%', display: 'block', opacity: 0.95 }} />
              </div>
            ))}

            {/* Image slot photo previews — show uploaded person photos at their frame positions */}
            {slotFiles.filter(s => s.previewUrl).map((s, idx) => (
              <div key={s.id || idx} style={{
                position: 'absolute',
                top: `${s.top_pct}%`, left: `${s.left_pct}%`,
                width: `${s.width_pct}%`,
                height: canvasHeight > 0 ? `${(s.height_pct / 100) * canvasHeight}px` : 80,
                overflow: 'hidden', zIndex: 12, pointerEvents: 'none',
                borderRadius: `${s.corner_radius || 0}%`,
                boxShadow: (s.border_width > 0)
                  ? `inset 0 0 0 ${s.border_width}px ${s.border_color || '#fff'}`
                  : 'none',
              }}>
                <img src={s.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            ))}
          </div>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            Live preview — values update as you type
          </Text>
        </Card>
      </Col>

      {/* Right: fill values */}
      <Col xs={24} lg={10}>
        {!selTemplate ? (
          <Card>
            <Empty description="Select a template on the left to get started" />
          </Card>
        ) : (
          <>
            {/* Image slots — person photo upload */}
            {slotFiles.length > 0 && (
              <Card
                title={<><BlockOutlined style={{ marginRight: 6, color: '#1890ff' }} />Upload Person Photo</>}
                style={{ marginBottom: 16 }}
              >
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                  Upload a photo — it will be cropped to fit exactly in the defined frame on the poster.
                </Text>
                {slotFiles.map((s, idx) => {
                  if (!slotInputRefs.current[idx]) slotInputRefs.current[idx] = React.createRef();
                  return (
                    <div key={s.id || idx} style={{ marginBottom: 14 }}>
                      <Text style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                        {s.label || `Photo ${idx + 1}`}
                        <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
                          Frame: {Math.round(s.width_pct)}% wide × {Math.round(s.height_pct)}% tall
                        </Text>
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                          ref={slotInputRefs.current[idx]}
                          type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={(e) => handleSlotFile(idx, e)}
                        />
                        {s.previewUrl ? (
                          <div style={{ position: 'relative' }}>
                            <img
                              src={s.previewUrl} alt=""
                              style={{
                                width: 80, height: 80, objectFit: 'cover',
                                borderRadius: 6, border: '2px solid #1890ff',
                              }}
                            />
                            <Button
                              size="small" type="text" danger icon={<DeleteOutlined />}
                              style={{ position: 'absolute', top: -8, right: -8, background: '#fff', borderRadius: '50%', border: '1px solid #ff4d4f', width: 20, height: 20, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onClick={() => {
                                URL.revokeObjectURL(s.previewUrl);
                                setSlotFiles(prev => prev.map((x, i) => i === idx ? { ...x, file: null, previewUrl: null } : x));
                              }}
                            />
                          </div>
                        ) : null}
                        <Button
                          icon={<UploadOutlined />}
                          size="large"
                          type={s.file ? 'default' : 'primary'}
                          onClick={() => slotInputRefs.current[idx]?.current?.click()}
                          style={{ flex: 1 }}
                        >
                          {s.file ? 'Change Photo' : 'Upload Photo'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}

            {/* Token values */}
            {tokens.length > 0 && (
              <Card
                title="Fill Details"
                extra={<Text type="secondary" style={{ fontSize: 11 }}>Type below — canvas updates live</Text>}
                style={{ marginBottom: 16 }}
              >
                {tokens.map((token, idx) => {
                  const label = tokenLabel(token);
                  return (
                    <div key={token} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', background: '#1890ff',
                          color: '#fff', fontSize: 11, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          {idx + 1}
                        </div>
                        <Text style={{ fontSize: 14, fontWeight: 600 }}>{label}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>→ placed on poster at field {idx + 1} position</Text>
                      </div>
                      <Input
                        size="large"
                        value={genValues[token] || ''}
                        onChange={e => setGenValues(prev => ({ ...prev, [token]: e.target.value }))}
                        placeholder={`Type ${label} here...`}
                        allowClear
                      />
                    </div>
                  );
                })}
              </Card>
            )}

            {/* Action */}
            <Card>
              <Button
                block size="large" icon={<EyeOutlined />}
                loading={previewing} onClick={handlePreview}
                style={{ marginBottom: 10 }}
              >
                Preview Poster
              </Button>
              <Button
                block type="primary" size="large" icon={<DownloadOutlined />}
                loading={downloading} onClick={handleDownload}
              >
                Generate &amp; Download
              </Button>
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message="After download, values clear automatically — just upload the next photo and fill details."
              />
            </Card>
          </>
        )}
      </Col>

      {/* Preview Modal */}
      <Modal
        open={previewModal}
        onCancel={() => setPreviewModal(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModal(false)}>Close</Button>,
          <Button key="dl" type="primary" icon={<DownloadOutlined />}
            onClick={() => {
              const name = `${genValues[tokens[0]] || selTemplate?.name || 'poster'}.jpg`;
              triggerDownload(previewBlobUrl, name);
              notification.success({ message: 'Downloaded!' });
            }}>
            Download This
          </Button>,
        ]}
        title="Poster Preview"
        width="90vw" style={{ top: 20 }}
        bodyStyle={{ padding: 8, textAlign: 'center', background: '#111' }}
      >
        {previewBlobUrl && (
          <img src={previewBlobUrl} alt="Preview"
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }} />
        )}
      </Modal>
    </Row>
  );

}

// ─────────────────────────────────────────────────────────────────────────────
// BUILDER — design templates
// ─────────────────────────────────────────────────────────────────────────────
function Builder({ templates, fetchTemplates }) {
  const [posterTab, setPosterTab]           = useState('library');
  const [posters, setPosters]               = useState([]);
  const [selectedPoster, setSelectedPoster] = useState(null);
  const [uploadedFile, setUploadedFile]     = useState(null);
  const [uploadedUrl, setUploadedUrl]       = useState(null);

  const [fields, setFields]         = useState([]);
  const [values, setValues]         = useState({});
  const [selectedId, setSelectedId] = useState(null);

  const [overlays, setOverlays]         = useState([]);
  const [selectedOvId, setSelectedOvId] = useState(null);

  // Image slots — defined rectangular frames for person photos
  const [imageSlots, setImageSlots]         = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [resizingSlot, setResizingSlot]     = useState(null); // { id, startX, startY, origW, origH }

  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const containerRef = useRef(null);

  const [downloading, setDownloading]       = useState(false);
  const [previewing, setPreviewing]         = useState(false);
  const [previewModal, setPreviewModal]     = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [canvasWidth, setCanvasWidth]       = useState(0);
  const [canvasHeight, setCanvasHeight]     = useState(0);
  const [showLive, setShowLive]             = useState(false);

  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [newTemplateName, setNewTemplateName]   = useState('');
  const [saving, setSaving]                     = useState(false);

  const draftRestoredRef = useRef(false);
  const tokens = extractTokens(fields);

  useEffect(() => {
    if (!containerRef.current) return;
    const measure = () => setCanvasWidth(containerRef.current?.offsetWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    axios.get(`${baseUrl}/poster-config/list`, { headers: authHeaders() })
      .then(res => { if (res.data.status) setPosters(res.data.result || []); })
      .catch(() => {});
  }, []);

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.posterTab) setPosterTab(d.posterTab);
        if (d.selectedPoster) setSelectedPoster(d.selectedPoster);
        if (Array.isArray(d.fields) && d.fields.length) setFields(d.fields);
        if (d.values) setValues(d.values);
        if (Array.isArray(d.overlays) && d.overlays.length)
          setOverlays(d.overlays.map(ov => ({ ...ov, file: null, previewUrl: null })));
        if (Array.isArray(d.imageSlots) && d.imageSlots.length)
          setImageSlots(d.imageSlots);
      }
    } catch (_) {}
    draftRestoredRef.current = true;
  }, []); // eslint-disable-line

  // Auto-save draft
  useEffect(() => {
    if (!draftRestoredRef.current) return;
    const draft = {
      posterTab, selectedPoster, fields, values,
      overlays: overlays.map(({ file, previewUrl, ...rest }) => rest),
      imageSlots,
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [posterTab, selectedPoster, fields, values, overlays, imageSlots]);

  // Sync tokens
  useEffect(() => {
    setValues(prev => {
      const next = { ...prev };
      tokens.forEach(t => { if (!(t in next)) next[t] = ''; });
      return next;
    });
  }, [tokens.join(',')]); // eslint-disable-line

  useEffect(() => () => {
    if (uploadedUrl) URL.revokeObjectURL(uploadedUrl);
    overlays.forEach(ov => { if (ov.previewUrl) URL.revokeObjectURL(ov.previewUrl); });
  }, []); // eslint-disable-line

  // Drag (fields, overlays, image slots)
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragging.startX) / rect.width)  * 100;
      const dy = ((e.clientY - dragging.startY) / rect.height) * 100;
      const newTop  = Math.max(0, Math.min(99,  dragging.origTop  + dy));
      const newLeft = Math.max(0, Math.min(100, dragging.origLeft + dx));
      if (dragging.type === 'overlay')
        setOverlays(prev => prev.map(ov => ov.id === dragging.id ? { ...ov, top_pct: newTop, left_pct: newLeft } : ov));
      else if (dragging.type === 'slot')
        setImageSlots(prev => prev.map(s => s.id === dragging.id ? { ...s, top_pct: newTop, left_pct: newLeft } : s));
      else
        setFields(prev => prev.map(f => f.id === dragging.id ? { ...f, top_pct: newTop, left_pct: newLeft } : f));
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  // Resize image slot (drag bottom-right corner — changes width_pct and height_pct)
  useEffect(() => {
    if (!resizingSlot) return;
    const onMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dxPct = ((e.clientX - resizingSlot.startX) / rect.width)  * 100;
      const dyPct = ((e.clientY - resizingSlot.startY) / rect.height) * 100;
      const newW = Math.max(5, Math.min(95, parseFloat((resizingSlot.origW + dxPct).toFixed(1))));
      const newH = Math.max(5, Math.min(95, parseFloat((resizingSlot.origH + dyPct).toFixed(1))));
      setImageSlots(prev => prev.map(s => s.id === resizingSlot.id ? { ...s, width_pct: newW, height_pct: newH } : s));
    };
    const onUp = () => setResizingSlot(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizingSlot]);

  // Resize
  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - resizing.startX) / rect.width) * 100;
      const newSize = Math.max(2, Math.min(100, parseFloat((resizing.origSize + dx).toFixed(1))));
      setOverlays(prev => prev.map(ov => ov.id === resizing.id ? { ...ov, size_pct: newSize } : ov));
    };
    const onUp = () => setResizing(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizing]);

  const startDragField = useCallback((e, id) => {
    e.preventDefault(); e.stopPropagation();
    const f = fields.find(f => f.id === id);
    if (!f) return;
    setSelectedId(id); setSelectedOvId(null);
    setDragging({ id, type: 'field', startX: e.clientX, startY: e.clientY, origTop: f.top_pct, origLeft: f.left_pct ?? 50 });
  }, [fields]);

  const startDragOverlay = useCallback((e, id) => {
    e.preventDefault(); e.stopPropagation();
    const ov = overlays.find(o => o.id === id);
    if (!ov) return;
    setSelectedOvId(id); setSelectedId(null);
    setDragging({ id, type: 'overlay', startX: e.clientX, startY: e.clientY, origTop: ov.top_pct, origLeft: ov.left_pct });
  }, [overlays]);

  const addField = () => {
    // Auto-assign numbered token so users don't need to type {name} manually
    setFields(prev => {
      const nextNum = prev.length + 1;
      const token = `field_${nextNum}`;
      const f = { id: genId(), template: `{${token}}`, top_pct: 50, left_pct: 50, font_size: 60, color: '#000000', bold: true, text_anchor: 'middle', font_family: DEFAULT_FONT };
      setSelectedId(f.id);
      return [...prev, f];
    });
  };
  const updateField = (id, key, val) => setFields(prev => prev.map(f => f.id === id ? { ...f, [key]: val } : f));
  const removeField = (id) => { setFields(prev => prev.filter(f => f.id !== id)); if (selectedId === id) setSelectedId(null); };
  const sel = fields.find(f => f.id === selectedId);

  // ── Image slot operations ──────────────────────────────────────────────────
  const addImageSlot = () => {
    const s = { id: genId(), label: `Photo ${imageSlots.length + 1}`, top_pct: 10, left_pct: 25, width_pct: 50, height_pct: 40, border_width: 0, border_color: '#ffffff', corner_radius: 0 };
    setImageSlots(prev => [...prev, s]);
    setSelectedSlotId(s.id);
    setSelectedId(null); setSelectedOvId(null);
  };
  const updateSlot = (id, key, val) => setImageSlots(prev => prev.map(s => s.id === id ? { ...s, [key]: val } : s));
  const removeSlot = (id) => { setImageSlots(prev => prev.filter(s => s.id !== id)); if (selectedSlotId === id) setSelectedSlotId(null); };
  const selSlot = imageSlots.find(s => s.id === selectedSlotId);

  const handlePosterSelect = (posterId) => {
    if (!posterId) { setSelectedPoster(null); return; }
    const p = posters.find(p => String(p.id) === String(posterId));
    if (!p) return;
    const imageUrl  = buildImageUrl(p.poster_image);
    const imagePath = p.poster_image || imageUrl;
    setSelectedPoster({ id: p.id, imageUrl, imagePath, name: p.poster_name });
    if (p.text_config) {
      try {
        const tc = typeof p.text_config === 'string' ? JSON.parse(p.text_config) : p.text_config;
        if (tc?.rows?.length)
          setFields(tc.rows.map(r => ({ left_pct: 50, text_anchor: 'middle', font_family: DEFAULT_FONT, bold: true, color: '#000000', font_size: 60, ...r, id: r.id || genId() })));
      } catch (_) {}
    }
  };

  const handlePosterFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploadedUrl) URL.revokeObjectURL(uploadedUrl);
    setUploadedFile(file);
    setUploadedUrl(URL.createObjectURL(file));
  };

  const addOverlay = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ov = { id: genId(), file, previewUrl: URL.createObjectURL(file), name: file.name, top_pct: 5, left_pct: 5, size_pct: 20 };
    setOverlays(prev => [...prev, ov]);
    setSelectedOvId(ov.id);
    e.target.value = '';
  };

  const reUploadOverlay = (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOverlays(prev => prev.map(ov => ov.id === id ? { ...ov, file, previewUrl: URL.createObjectURL(file), name: file.name } : ov));
    e.target.value = '';
  };

  const updateOverlay = (id, key, val) => setOverlays(prev => prev.map(ov => ov.id === id ? { ...ov, [key]: val } : ov));
  const removeOverlay = (id) => {
    const ov = overlays.find(o => o.id === id);
    if (ov?.previewUrl) URL.revokeObjectURL(ov.previewUrl);
    setOverlays(prev => prev.filter(o => o.id !== id));
    if (selectedOvId === id) setSelectedOvId(null);
  };
  const selOv = overlays.find(o => o.id === selectedOvId);
  const canvasImageUrl = posterTab === 'upload' ? uploadedUrl : selectedPoster?.imageUrl;

  const buildFormData = () => {
    const fd = new FormData();
    if (posterTab === 'upload' && uploadedFile) fd.append('poster_file', uploadedFile);
    else fd.append('poster_path', selectedPoster.imagePath);
    fd.append('text_config', JSON.stringify({ rows: fields }));
    fd.append('member_data', JSON.stringify(values));
    // Combine regular overlays + image slots (slots go after overlays)
    const allConfigs = [
      ...overlays.map((ov, i) => ({ index: i, top_pct: ov.top_pct, left_pct: ov.left_pct, size_pct: ov.size_pct })),
      ...imageSlots.map((s, i) => ({ index: overlays.length + i, top_pct: s.top_pct, left_pct: s.left_pct, width_pct: s.width_pct, height_pct: s.height_pct, border_width: s.border_width || 0, border_color: s.border_color || '#ffffff', corner_radius: s.corner_radius || 0 })),
    ];
    fd.append('overlay_configs', JSON.stringify(allConfigs));
    overlays.forEach((ov, i) => { if (ov.file) fd.append(`overlay_${i}`, ov.file, ov.name || `ov_${i}.png`); });
    // Image slots don't have files in the builder test — skip them
    return fd;
  };

  const posterFileName = `${(posterTab === 'library' ? selectedPoster?.name : uploadedFile?.name?.replace(/\.[^.]+$/, '')) || 'poster'}.jpg`;

  const validate = () => {
    const hasImage = posterTab === 'upload' ? !!uploadedFile : !!selectedPoster;
    if (!hasImage) { notification.warning({ message: 'Select or upload a base poster first' }); return false; }
    if (!fields.length && !overlays.length) { notification.warning({ message: 'Add at least one text field or image overlay' }); return false; }
    return true;
  };

  const handlePreview = async () => {
    if (!validate()) return;
    setPreviewing(true);
    try {
      const res = await axios.post(`${baseUrl}/poster/generate-download`, buildFormData(), { headers: authHeaders(), responseType: 'blob' });
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' })));
      setPreviewModal(true);
    } catch (err) { notification.error({ message: 'Preview failed: ' + err.message }); }
    setPreviewing(false);
  };

  const triggerDownload = (url, name) => {
    const a = document.createElement('a'); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleDownload = async () => {
    if (!validate()) return;
    setDownloading(true);
    try {
      const res = await axios.post(`${baseUrl}/poster/generate-download`, buildFormData(), { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' }));
      triggerDownload(url, posterFileName);
      URL.revokeObjectURL(url);
      notification.success({ message: 'Downloaded!' });
    } catch (err) { notification.error({ message: 'Failed: ' + err.message }); }
    setDownloading(false);
  };

  const confirmSaveTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) { notification.warning({ message: 'Enter a template name' }); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        poster_tab: posterTab,
        poster_id:   selectedPoster?.id   || null,
        poster_name: selectedPoster?.name || null,
        poster_path: selectedPoster?.imagePath || null,
        fields_config: fields,
        // Save regular overlays + image slots together; slots marked with is_slot:true
        overlay_config: [
          ...overlays.map(({ file, previewUrl, ...rest }) => ({ ...rest, is_slot: false })),
          ...imageSlots.map(s => ({ ...s, is_slot: true })),
        ],
      };
      const res = await axios.post(`${baseUrl}/poster-builder-template/save`, payload, { headers: authHeaders() });
      if (res.data.status) {
        notification.success({ message: `Template "${name}" saved!` });
        setSaveModalVisible(false);
        fetchTemplates();
      } else notification.error({ message: res.data.message || 'Save failed' });
    } catch (err) { notification.error({ message: 'Save failed: ' + err.message }); }
    setSaving(false);
  };

  const clearDraft = () => {
    setFields([]); setValues({}); setOverlays([]); setImageSlots([]); setSelectedPoster(null);
    setUploadedFile(null); setUploadedUrl(null); setPosterTab('library');
    setSelectedId(null); setSelectedOvId(null); setSelectedSlotId(null);
    localStorage.removeItem(DRAFT_KEY);
    notification.info({ message: 'Canvas cleared' });
  };

  const overlayInputRef = useRef(null);
  const posterInputRef  = useRef(null);
  const reUploadRefs    = useRef({});
  const getReUploadRef  = (id) => {
    if (!reUploadRefs.current[id]) reUploadRefs.current[id] = React.createRef();
    return reUploadRefs.current[id];
  };

  return (
    <>
      <Row gutter={20}>
        {/* Canvas */}
        <Col xs={24} lg={14}>
          <Card style={{ marginBottom: 16 }}>
            <Text strong>1. Select Base Poster</Text>
            <Tabs activeKey={posterTab} onChange={setPosterTab} size="small" style={{ marginTop: 8 }}>
              <TabPane tab="From Library" key="library">
                <Select
                  placeholder="Choose from poster library..."
                  style={{ width: '100%' }}
                  onChange={handlePosterSelect}
                  value={selectedPoster?.id ? String(selectedPoster.id) : undefined}
                  allowClear onClear={() => setSelectedPoster(null)}
                  showSearch optionFilterProp="children"
                >
                  {posters.map(p => (
                    <Option key={p.id} value={String(p.id)}>{p.poster_name || `Poster #${p.id}`}</Option>
                  ))}
                </Select>
              </TabPane>
              <TabPane tab="Upload Image" key="upload">
                <input ref={posterInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePosterFileChange} />
                <Button icon={<UploadOutlined />} onClick={() => posterInputRef.current?.click()}>
                  {uploadedFile ? uploadedFile.name : 'Choose Image File'}
                </Button>
                {uploadedFile && <Tag color="green" style={{ marginLeft: 8 }}>Ready</Tag>}
              </TabPane>
            </Tabs>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Text strong>2. Drag to Position Fields &amp; Images</Text>
              <Tooltip title={showLive ? 'Switch to edit mode (show field borders)' : 'Preview mode (hide borders, show values)'}>
                <Switch
                  size="small"
                  checked={showLive}
                  onChange={setShowLive}
                  checkedChildren="Live"
                  unCheckedChildren="Edit"
                />
              </Tooltip>
            </div>

            <div
              ref={containerRef}
              style={{
                position: 'relative', width: '100%', marginTop: 4,
                border: '2px solid #1890ff', borderRadius: 6,
                overflow: 'hidden', cursor: showLive ? 'default' : 'crosshair',
                userSelect: 'none', background: '#ddd', minHeight: 300,
              }}
              onClick={(e) => {
                if (!showLive && (e.target === containerRef.current || e.target.tagName === 'IMG')) {
                  setSelectedId(null); setSelectedOvId(null);
                }
              }}
            >
              {canvasImageUrl
                ? <img src={canvasImageUrl} alt="Poster" style={{ width: '100%', display: 'block' }} draggable={false}
                    onLoad={(e) => setCanvasHeight(e.target.offsetHeight)} />
                : (
                  <div style={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Empty description="Select or upload a poster above" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </div>
                )
              }

              {/* Image slot rectangles */}
              {!showLive && imageSlots.map(s => {
                const isSel = selectedSlotId === s.id;
                const slotH = canvasHeight > 0 ? (s.height_pct / 100) * canvasHeight : 80;
                return (
                  <div
                    key={s.id}
                    onMouseDown={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      setSelectedSlotId(s.id); setSelectedId(null); setSelectedOvId(null);
                      setDragging({ id: s.id, type: 'slot', startX: e.clientX, startY: e.clientY, origTop: s.top_pct, origLeft: s.left_pct });
                    }}
                    style={{
                      position: 'absolute',
                      top: `${s.top_pct}%`,
                      left: `${s.left_pct}%`,
                      width: `${s.width_pct}%`,
                      height: slotH,
                      border: `2px dashed ${isSel ? '#1890ff' : 'rgba(24,144,255,0.7)'}`,
                      background: isSel ? 'rgba(24,144,255,0.12)' : 'rgba(24,144,255,0.06)',
                      cursor: 'grab', zIndex: isSel ? 25 : 15,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxSizing: 'border-box', borderRadius: `${s.corner_radius || 0}%`,
                    }}
                  >
                    <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
                      <BlockOutlined style={{ fontSize: 18, color: '#1890ff', display: 'block', marginBottom: 2 }} />
                      <span style={{ fontSize: 10, color: '#1890ff', fontWeight: 600 }}>{s.label}</span>
                    </div>
                    {/* Bottom-right resize handle */}
                    {isSel && (
                      <div
                        onMouseDown={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setResizingSlot({ id: s.id, startX: e.clientX, startY: e.clientY, origW: s.width_pct, origH: s.height_pct });
                        }}
                        style={{
                          position: 'absolute', bottom: -5, right: -5,
                          width: 14, height: 14, background: '#1890ff',
                          border: '2px solid #fff', borderRadius: 3, cursor: 'se-resize', zIndex: 30,
                        }}
                      />
                    )}
                  </div>
                );
              })}

              {fields.map(f => {
                const isSelected = selectedId === f.id;
                const scaledSize = Math.max(6, Math.round(f.font_size * (canvasWidth > 0 ? canvasWidth / 2400 : 0.07)));
                return (
                  <div
                    key={f.id}
                    onMouseDown={showLive ? undefined : (e) => startDragField(e, f.id)}
                    style={{
                      position: 'absolute',
                      top: `${f.top_pct}%`,
                      left: `${f.left_pct ?? 50}%`,
                      transform: (f.text_anchor || 'middle') === 'middle' ? 'translateX(-50%)'
                        : f.text_anchor === 'end' ? 'translateX(-100%)' : 'none',
                      cursor: showLive ? 'default' : (dragging?.id === f.id ? 'grabbing' : 'grab'),
                      padding: showLive ? '0' : '2px 6px',
                      background: showLive ? 'transparent' : (isSelected ? 'rgba(24,144,255,0.18)' : 'rgba(255,255,255,0.12)'),
                      border: showLive ? 'none' : `1px dashed ${isSelected ? '#1890ff' : 'rgba(0,0,0,0.3)'}`,
                      borderRadius: 3, whiteSpace: 'nowrap',
                      fontSize: scaledSize,
                      fontWeight: f.bold ? 'bold' : 'normal',
                      fontFamily: `'${f.font_family || DEFAULT_FONT}', Arial, sans-serif`,
                      color: f.color || '#000',
                      zIndex: isSelected ? 20 : 10,
                      textShadow: showLive ? '0 0 2px rgba(255,255,255,0.4)' : 'none',
                    }}
                  >
                    {(() => {
                      const v = renderWithValues(f.template, values);
                      if (showLive) return v || '';
                      // In edit mode: show value if typed, else show friendly label
                      if (v) return v;
                      const label = (f.template || '').replace(/^\{(\w+)\}$/, (_, k) => k.replace(/_/g, ' ')) || 'empty';
                      return <span style={{ color: isSelected ? '#1890ff' : '#888', fontStyle: 'italic', fontSize: Math.max(6, scaledSize - 2) }}>{label}</span>;
                    })()}
                  </div>
                );
              })}

              {overlays.filter(ov => ov.previewUrl).map(ov => {
                const isSelOv = selectedOvId === ov.id;
                return (
                  <div key={ov.id}
                    style={{ position: 'absolute', top: `${ov.top_pct}%`, left: `${ov.left_pct}%`, width: `${ov.size_pct}%`, zIndex: isSelOv ? 20 : 10 }}
                  >
                    <img
                      src={ov.previewUrl} alt={ov.name} draggable={false}
                      onMouseDown={showLive ? undefined : (e) => startDragOverlay(e, ov.id)}
                      style={{
                        width: '100%', display: 'block', opacity: showLive ? 0.95 : 0.9,
                        cursor: showLive ? 'default' : (dragging?.id === ov.id ? 'grabbing' : 'grab'),
                        border: showLive ? 'none' : `2px ${isSelOv ? 'solid #52c41a' : 'dashed rgba(82,196,26,0.6)'}`,
                        borderRadius: 4, boxSizing: 'border-box',
                      }}
                    />
                    {!showLive && isSelOv && (
                      <div
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setResizing({ id: ov.id, startX: e.clientX, origSize: ov.size_pct }); }}
                        style={{ position: 'absolute', bottom: -6, right: -6, width: 14, height: 14, background: '#52c41a', border: '2px solid #fff', borderRadius: 3, cursor: 'se-resize', zIndex: 30 }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              {showLive ? 'Live preview — toggle to Edit mode to drag and reposition elements' : 'Edit mode — drag text/images to reposition | toggle to Live to preview without borders'}
            </Text>
          </Card>
        </Col>

        {/* Controls */}
        <Col xs={24} lg={10}>

          {/* Text fields */}
          <Card
            title={`Text Fields (${fields.length})`}
            extra={
              <Space size={4}>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addField}>Add Field</Button>
                <Button size="small" icon={<SaveOutlined />} type="primary" ghost onClick={() => { setNewTemplateName(''); setSaveModalVisible(true); }}>
                  Save Template
                </Button>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            {fields.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>Click "Add Field" to place text on the poster.</Text>
            )}
            {fields.map((f, i) => (
              <div
                key={f.id}
                onClick={() => { setSelectedId(f.id); setSelectedOvId(null); }}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '4px 8px', marginBottom: 4, cursor: 'pointer', borderRadius: 4,
                  border: `1px solid ${selectedId === f.id ? '#1890ff' : '#e8e8e8'}`,
                  background: selectedId === f.id ? '#e6f4ff' : '#fff',
                }}
              >
                <Text ellipsis style={{ maxWidth: 200, fontSize: 12 }}>
                  <span style={{ color: '#aaa', marginRight: 4 }}>{i + 1}.</span>
                  {f.template
                    ? <span style={{ color: '#1890ff', fontWeight: 500 }}>
                        {f.template.replace(/^\{(\w+)\}$/, (_, k) => k.replace(/_/g, ' ')) || f.template}
                      </span>
                    : <span style={{ color: '#ccc' }}>(no label)</span>
                  }
                </Text>
                <Button size="small" type="text" danger icon={<DeleteOutlined />}
                  onClick={(e) => { e.stopPropagation(); removeField(f.id); }} />
              </div>
            ))}

            {sel && (
              <div style={{ marginTop: 10, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6, background: '#fafafa' }}>
                <Text strong style={{ color: '#1890ff', display: 'block', marginBottom: 8 }}>
                  Editing Field #{fields.findIndex(f => f.id === sel.id) + 1}
                </Text>

                {/* Field Label — user types what this field represents (e.g. "name", "father name") */}
                <Text style={{ fontSize: 12, fontWeight: 600 }}>Field Label</Text>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                  Type what this field represents, e.g. <b>name</b>, <b>father name</b>, <b>village</b>, <b>date</b>
                </Text>
                <Input
                  value={
                    // Extract the label from template: {father_name} → "father name"
                    (sel.template || '').replace(/^\{(\w+)\}$/, (_, k) => k.replace(/_/g, ' '))
                  }
                  onChange={e => {
                    // Auto-wrap in {token}: "father name" → {father_name}
                    const label = e.target.value;
                    const token = label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                    updateField(selectedId, 'template', token ? `{${token}}` : '');
                  }}
                  placeholder="e.g. name, father name, village, date..."
                  size="large"
                  style={{ marginBottom: 6 }}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                  <Text type="secondary" style={{ fontSize: 11, width: '100%' }}>Quick select:</Text>
                  {QUICK_TOKENS.map(t => (
                    <Tag key={t} color="blue" style={{ cursor: 'pointer', fontSize: 11 }}
                      onClick={() => updateField(selectedId, 'template', `{${t}}`)}>
                      {t.replace(/_/g, ' ')}
                    </Tag>
                  ))}
                </div>

                <Select value={sel.font_family || DEFAULT_FONT} onChange={v => updateField(selectedId, 'font_family', v)} style={{ width: '100%', marginBottom: 8 }} size="small">
                  {FONT_FAMILIES.map(f => (
                    <Option key={f.value} value={f.value}><span style={{ fontFamily: `'${f.value}', Arial` }}>{f.label}</span></Option>
                  ))}
                </Select>

                <Row gutter={6} style={{ marginBottom: 8 }}>
                  <Col span={7}>
                    <Text style={{ fontSize: 11 }}>Size (px)</Text>
                    <InputNumber size="small" min={10} max={500} value={sel.font_size} onChange={v => updateField(selectedId, 'font_size', v)} style={{ width: '100%', marginTop: 2 }} />
                  </Col>
                  <Col span={7}>
                    <Text style={{ fontSize: 11 }}>Color</Text>
                    <input type="color" value={sel.color || '#000000'} onChange={e => updateField(selectedId, 'color', e.target.value)}
                      style={{ width: '100%', height: 26, marginTop: 2, padding: 1, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }} />
                  </Col>
                  <Col span={5}>
                    <Text style={{ fontSize: 11 }}>Bold</Text>
                    <div style={{ marginTop: 4 }}><Switch size="small" checked={sel.bold} onChange={v => updateField(selectedId, 'bold', v)} /></div>
                  </Col>
                  <Col span={5}>
                    <Text style={{ fontSize: 11 }}>Align</Text>
                    <Select size="small" value={sel.text_anchor || 'middle'} onChange={v => updateField(selectedId, 'text_anchor', v)} style={{ width: '100%', marginTop: 2 }}>
                      <Option value="start">L</Option>
                      <Option value="middle">C</Option>
                      <Option value="end">R</Option>
                    </Select>
                  </Col>
                </Row>

                <Row gutter={6} align="middle">
                  <Col span={7}>
                    <Text style={{ fontSize: 11 }}>Top %</Text>
                    <InputNumber size="small" min={0} max={99} precision={1} value={Math.round(sel.top_pct * 10) / 10} onChange={v => updateField(selectedId, 'top_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                  </Col>
                  <Col span={7}>
                    <Text style={{ fontSize: 11 }}>Left %</Text>
                    <InputNumber size="small" min={0} max={100} precision={1} value={Math.round((sel.left_pct ?? 50) * 10) / 10} onChange={v => updateField(selectedId, 'left_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                  </Col>
                  <Col span={10}>
                    <Text style={{ fontSize: 11 }}>Nudge</Text>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, marginTop: 2 }}>
                      <Button size="small" icon={<ArrowUpOutlined />} onClick={() => updateField(selectedId, 'top_pct', Math.max(0, sel.top_pct - 0.5))} />
                      <Space size={1}>
                        <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => updateField(selectedId, 'left_pct', Math.max(0, (sel.left_pct ?? 50) - 0.5))} />
                        <Button size="small" icon={<ArrowDownOutlined />} onClick={() => updateField(selectedId, 'top_pct', Math.min(99, sel.top_pct + 0.5))} />
                        <Button size="small" icon={<ArrowRightOutlined />} onClick={() => updateField(selectedId, 'left_pct', Math.min(100, (sel.left_pct ?? 50) + 0.5))} />
                      </Space>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Card>

          {/* Image Slots — defined frames for person photos */}
          <Card
            title={<><BlockOutlined style={{ marginRight: 6, color: '#1890ff' }} />Image Slots ({imageSlots.length})</>}
            extra={<Button size="small" type="primary" ghost icon={<PlusOutlined />} onClick={addImageSlot}>Add Slot</Button>}
            style={{ marginBottom: 16 }}
          >
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Define where the person's photo will appear. Drag the blue frame on the canvas to position, drag the corner to resize.
            </Text>
            {imageSlots.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>No image slots yet. Click "Add Slot" to define a photo area.</Text>
            )}
            {imageSlots.map((s, i) => (
              <div key={s.id}>
                <div
                  onClick={() => { setSelectedSlotId(s.id); setSelectedId(null); setSelectedOvId(null); }}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '4px 8px', marginBottom: 4, cursor: 'pointer', borderRadius: 4,
                    border: `1px solid ${selectedSlotId === s.id ? '#1890ff' : '#e8e8e8'}`,
                    background: selectedSlotId === s.id ? '#e6f4ff' : '#fff',
                  }}
                >
                  <Text style={{ fontSize: 12 }}>
                    <BlockOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                    <span style={{ fontWeight: 600 }}>{s.label}</span>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                      {Math.round(s.width_pct)}% × {Math.round(s.height_pct)}%
                    </Text>
                  </Text>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />}
                    onClick={(e) => { e.stopPropagation(); removeSlot(s.id); }} />
                </div>
                {selectedSlotId === s.id && selSlot && (
                  <div style={{ padding: 10, border: '1px solid #d0e8ff', borderRadius: 6, background: '#f0f7ff', marginBottom: 6 }}>
                    <Text style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 6, color: '#1890ff' }}>Edit Slot</Text>
                    <div style={{ marginBottom: 8 }}>
                      <Text style={{ fontSize: 11 }}>Label</Text>
                      <Input size="small" value={selSlot.label} onChange={e => updateSlot(s.id, 'label', e.target.value)} style={{ marginTop: 2 }} />
                    </div>
                    <Row gutter={6} style={{ marginBottom: 6 }}>
                      <Col span={12}>
                        <Text style={{ fontSize: 11 }}>Top %</Text>
                        <InputNumber size="small" min={0} max={90} precision={1} value={Math.round(selSlot.top_pct * 10) / 10} onChange={v => v != null && updateSlot(s.id, 'top_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                      </Col>
                      <Col span={12}>
                        <Text style={{ fontSize: 11 }}>Left %</Text>
                        <InputNumber size="small" min={0} max={90} precision={1} value={Math.round(selSlot.left_pct * 10) / 10} onChange={v => v != null && updateSlot(s.id, 'left_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                      </Col>
                    </Row>
                    <Row gutter={6} style={{ marginBottom: 6 }}>
                      <Col span={12}>
                        <Text style={{ fontSize: 11 }}>Width %</Text>
                        <InputNumber size="small" min={5} max={95} precision={1} value={Math.round(selSlot.width_pct * 10) / 10} onChange={v => v != null && updateSlot(s.id, 'width_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                      </Col>
                      <Col span={12}>
                        <Text style={{ fontSize: 11 }}>Height %</Text>
                        <InputNumber size="small" min={5} max={95} precision={1} value={Math.round(selSlot.height_pct * 10) / 10} onChange={v => v != null && updateSlot(s.id, 'height_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                      </Col>
                    </Row>
                    <Row gutter={6} style={{ marginBottom: 6 }}>
                      <Col span={12}>
                        <Text style={{ fontSize: 11 }}>Border Width (px)</Text>
                        <InputNumber size="small" min={0} max={30} precision={0} value={selSlot.border_width || 0} onChange={v => v != null && updateSlot(s.id, 'border_width', v)} style={{ width: '100%', marginTop: 2 }} />
                      </Col>
                      <Col span={12}>
                        <Text style={{ fontSize: 11 }}>Border Color</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <input
                            type="color"
                            value={selSlot.border_color || '#ffffff'}
                            onChange={e => updateSlot(s.id, 'border_color', e.target.value)}
                            style={{ width: 36, height: 28, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer', padding: 1 }}
                          />
                          <Input
                            size="small"
                            value={selSlot.border_color || '#ffffff'}
                            onChange={e => updateSlot(s.id, 'border_color', e.target.value)}
                            style={{ flex: 1 }}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={6}>
                      <Col span={12}>
                        <Text style={{ fontSize: 11 }}>Corner Radius %</Text>
                        <InputNumber size="small" min={0} max={50} precision={0} value={selSlot.corner_radius || 0} onChange={v => v != null && updateSlot(s.id, 'corner_radius', v)} style={{ width: '100%', marginTop: 2 }} />
                      </Col>
                      <Col span={12} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>0 = square · 50 = circle</Text>
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            ))}
          </Card>

          {/* Image overlays */}
          <Card
            title={<><PictureOutlined style={{ marginRight: 6 }} />Image Overlays ({overlays.length})</>}
            extra={
              <>
                <input ref={overlayInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={addOverlay} />
                <Button size="small" type="dashed" icon={<UploadOutlined />} onClick={() => overlayInputRef.current?.click()}>Add Image</Button>
              </>
            }
            style={{ marginBottom: 16 }}
          >
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Positions are saved in templates. Upload sample to calibrate, then use Quick Generate for real persons.
            </Text>

            {overlays.map(ov => (
              <div key={ov.id}>
                <div
                  onClick={() => { setSelectedOvId(ov.id); setSelectedId(null); }}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '4px 8px', marginBottom: 4, cursor: 'pointer', borderRadius: 4,
                    border: `1px solid ${selectedOvId === ov.id ? '#52c41a' : '#e8e8e8'}`,
                    background: selectedOvId === ov.id ? '#f6ffed' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {ov.previewUrl
                      ? <img src={ov.previewUrl} alt="" style={{ height: 28, width: 28, objectFit: 'contain', borderRadius: 3, border: '1px solid #eee' }} />
                      : <div style={{ height: 28, width: 28, background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ExclamationCircleOutlined style={{ color: '#fa8c16', fontSize: 14 }} /></div>
                    }
                    <div>
                      <Text ellipsis style={{ maxWidth: 120, fontSize: 12, display: 'block' }}>{ov.name}</Text>
                      {!ov.previewUrl && <Text style={{ fontSize: 10, color: '#fa8c16' }}>Re-upload needed</Text>}
                    </div>
                  </div>
                  <Space size={4}>
                    {!ov.previewUrl && (
                      <>
                        <input ref={getReUploadRef(ov.id)} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => reUploadOverlay(ov.id, e)} />
                        <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }}
                          onClick={(e) => { e.stopPropagation(); getReUploadRef(ov.id).current?.click(); }}>Upload</Button>
                      </>
                    )}
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); removeOverlay(ov.id); }} />
                  </Space>
                </div>
              </div>
            ))}

            {selOv && (
              <div style={{ marginTop: 10, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6, background: '#fafafa' }}>
                <Text strong style={{ color: '#52c41a', display: 'block', marginBottom: 8 }}>Editing: {selOv.name}</Text>
                <Row gutter={6} style={{ marginBottom: 6 }}>
                  <Col span={12}>
                    <Text style={{ fontSize: 11 }}>Top %</Text>
                    <InputNumber size="small" min={0} max={95} precision={1} value={Math.round(selOv.top_pct * 10) / 10} onChange={v => v != null && updateOverlay(selectedOvId, 'top_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                  </Col>
                  <Col span={12}>
                    <Text style={{ fontSize: 11 }}>Left %</Text>
                    <InputNumber size="small" min={0} max={95} precision={1} value={Math.round(selOv.left_pct * 10) / 10} onChange={v => v != null && updateOverlay(selectedOvId, 'left_pct', v)} style={{ width: '100%', marginTop: 2 }} />
                  </Col>
                </Row>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: 11 }}>Size (% of poster width)</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <Button size="small" icon={<ZoomOutOutlined />} onClick={() => updateOverlay(selectedOvId, 'size_pct', Math.max(2, parseFloat((selOv.size_pct - 2).toFixed(1))))}>Smaller</Button>
                    <InputNumber size="small" min={2} max={100} precision={1} value={Math.round(selOv.size_pct * 10) / 10} onChange={v => v != null && updateOverlay(selectedOvId, 'size_pct', v)} style={{ width: 70 }} formatter={v => `${v}%`} parser={v => v.replace('%', '')} />
                    <Button size="small" icon={<ZoomInOutlined />} onClick={() => updateOverlay(selectedOvId, 'size_pct', Math.min(100, parseFloat((selOv.size_pct + 2).toFixed(1))))}>Larger</Button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Button size="small" icon={<ArrowUpOutlined />} onClick={() => updateOverlay(selectedOvId, 'top_pct', Math.max(0, parseFloat((selOv.top_pct - 0.5).toFixed(1))))} />
                  <Space size={2}>
                    <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => updateOverlay(selectedOvId, 'left_pct', Math.max(0, parseFloat((selOv.left_pct - 0.5).toFixed(1))))} />
                    <Button size="small" icon={<ArrowDownOutlined />} onClick={() => updateOverlay(selectedOvId, 'top_pct', Math.min(95, parseFloat((selOv.top_pct + 0.5).toFixed(1))))} />
                    <Button size="small" icon={<ArrowRightOutlined />} onClick={() => updateOverlay(selectedOvId, 'left_pct', Math.min(95, parseFloat((selOv.left_pct + 0.5).toFixed(1))))} />
                  </Space>
                </div>
              </div>
            )}
          </Card>

          {/* Test generate */}
          <Card title="3. Test Generate" extra={<Button danger ghost size="small" onClick={clearDraft}>Clear</Button>}>
            {tokens.map(token => {
              const label = token.replace(/_/g, ' ');
              return (
                <div key={token} style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 3 }}>{label}</Text>
                  <Input value={values[token] || ''} onChange={e => setValues(prev => ({ ...prev, [token]: e.target.value }))} placeholder={`Type ${label}...`} size="small" />
                </div>
              );
            })}
            {tokens.length > 0 && <Divider style={{ margin: '12px 0' }} />}
            <Button block icon={<EyeOutlined />} loading={previewing} onClick={handlePreview} disabled={!canvasImageUrl} style={{ marginBottom: 8 }}>Preview Poster</Button>
            <Button type="primary" block icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload} disabled={!canvasImageUrl}>Generate &amp; Download</Button>
          </Card>
        </Col>
      </Row>

      {/* Preview Modal */}
      <Modal open={previewModal} onCancel={() => setPreviewModal(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModal(false)}>Close</Button>,
          <Button key="dl" type="primary" icon={<DownloadOutlined />} onClick={() => { triggerDownload(previewBlobUrl, posterFileName); notification.success({ message: 'Downloaded!' }); }}>Download This</Button>,
        ]}
        title="Poster Preview" width="90vw" style={{ top: 20 }}
        bodyStyle={{ padding: 8, textAlign: 'center', background: '#111' }}
      >
        {previewBlobUrl && <img src={previewBlobUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }} />}
      </Modal>

      {/* Save Template Modal */}
      <Modal open={saveModalVisible} onCancel={() => setSaveModalVisible(false)} onOk={confirmSaveTemplate}
        confirmLoading={saving} title="Save as Template" okText="Save" width={420}
      >
        <div style={{ marginBottom: 8 }}>
          <Text style={{ fontSize: 12 }}>Template Name</Text>
          <Input style={{ marginTop: 6 }} placeholder="e.g. Birthday Poster - Hindi" value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} onPressEnter={confirmSaveTemplate} autoFocus />
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Saves: poster selection, field positions &amp; styles, image overlay positions.<br />
          Does NOT save: token values or image files.
        </Text>
      </Modal>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function PosterBuilderPage() {
  const history = useHistory();
  const [mode, setMode]                         = useState('generate'); // 'builder' | 'generate'
  const [templates, setTemplates]               = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const fetchTemplates = useCallback(() => {
    setTemplatesLoading(true);
    axios.get(`${baseUrl}/poster-builder-template/list`, { headers: authHeaders() })
      .then(res => { if (res.data.status) setTemplates(res.data.result || []); })
      .catch(() => {})
      .finally(() => setTemplatesLoading(false));
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Button icon={<BackIcon />} onClick={() => history.push('/poster')}>Back</Button>
        <Title level={4} style={{ margin: 0, flex: 1 }}>
          <DragOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Poster Builder
        </Title>
        <Space>
          <Button
            type={mode === 'generate' ? 'primary' : 'default'}
            icon={<ThunderboltOutlined />}
            onClick={() => setMode('generate')}
          >
            Quick Generate
          </Button>
          <Button
            type={mode === 'builder' ? 'primary' : 'default'}
            icon={<ToolOutlined />}
            onClick={() => setMode('builder')}
          >
            Builder
          </Button>
        </Space>
      </div>

      {mode === 'generate' && (
        <QuickGenerate
          templates={templates}
          templatesLoading={templatesLoading}
          fetchTemplates={fetchTemplates}
        />
      )}
      {mode === 'builder' && (
        <Builder templates={templates} fetchTemplates={fetchTemplates} />
      )}
    </div>
  );
}
